import { useEffect } from 'react';
import { UseFormReturn, useFieldArray, useWatch } from 'react-hook-form';

import { AnimatePresence } from 'framer-motion';
import { Plus } from 'lucide-react';

import { Button } from 'components/Button';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';

import { QuickQuoteInputs, ThreeGCommentItem } from '../../types';
import { CommentCard } from './CommentCard';
import { useCommentTypes } from './hooks/useCommentTypes';

interface CommentsAndSpecialInstructionsSectionProps {
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
}

export function CommentsAndSpecialInstructionsSection({
  formMethods,
}: CommentsAndSpecialInstructionsSectionProps) {
  const { control, setValue } = formMethods;
  const { options: commentTypeOptions, isLoading } = useCommentTypes();
  const ltl = useWatch({ control, name: 'ltl' });
  const comments = useWatch({ control, name: 'ltl.comments' });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'ltl.comments',
  });

  useEffect(() => {
    if (!ltl) {
      return;
    }

    if (!Array.isArray(comments)) {
      setValue('ltl.comments', []);
    }
  }, [comments, ltl, setValue]);

  const handleAddComment = () => {
    const newComment: ThreeGCommentItem = {
      id: crypto.randomUUID(),
    };

    append(newComment);
  };

  const commentTypeSelectOptions = commentTypeOptions.map((opt) => ({
    label: opt.label,
    value: opt.value,
  }));

  return (
    <Flex direction='col' gap='md' className='w-full mx-0'>
      <Typography
        variant='h5'
        className='text-neutral-800 font-semibold whitespace-nowrap'
      >
        Comments & Special Instructions
      </Typography>

      <Flex align='center' justify='between' className='w-full'>
        <Typography variant='body' weight='medium'>
          Comments
        </Typography>
        <Button
          type='button'
          variant='outline'
          size='sm'
          onClick={handleAddComment}
          className='h-8 px-3 text-xs'
          buttonNamePosthog={null}
        >
          <Plus className='h-4 w-4 mr-1' />
          Add Comment
        </Button>
      </Flex>

      {fields.length > 0 && (
        <AnimatePresence>
          <Flex direction='col' gap='md' className='w-full'>
            {fields.map((field, index) => (
              <CommentCard
                key={field.id}
                index={index}
                formMethods={formMethods}
                onRemove={() => remove(index)}
                commentTypeOptions={commentTypeSelectOptions}
                isLoadingCommentTypes={isLoading}
              />
            ))}
          </Flex>
        </AnimatePresence>
      )}
    </Flex>
  );
}
